import {
  isInaccessibleAnalysisTemplateConfiguration,
  isInaccessibleProcedureTestDefinition,
  ProcedureFlowchart,
} from 'procedures/ProcedureDomainTypes';
import {getRequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';

export const enum ProcedureAccessLevel {
  READ_ONLY_HIDE_DETAILS = 'HIDE_DETAILS',
  FULL_ACCESS = 'FULL',
}

/**
 * Interrogates sub-objects in a procedure, looking for anything for which the user
 * does not have permissions.
 *
 * For now, the only objects that will surface permission issues are the linked schemas
 * associated with material inputs, outputs, equipment, analysis templates, and test definitions.
 *
 * And for now, we only implement two levels of access. Without access to all schemas,
 * we have to block the user from viewing the DETAILS page. Otherwise, full access.
 * In the future, there's no reason to remain so coarse.
 */
export default function determineProcedureAccessLevel(
  flowchart: ProcedureFlowchart,
  tagSchemas: Array<{id: string}>
): ProcedureAccessLevel {
  for (const node of flowchart.nodes) {
    const allLinkedSchemaIds: Array<string | undefined> = [
      ...node.materialOutputSettings.map(
        (materialOutput) => getRequiredLinkTagSchema(materialOutput.materialOutputVersion.dataType)?.id
      ),
      ...node.materialInputSettings.map(
        (materialInput) => getRequiredLinkTagSchema(materialInput.materialInputVersion.dataType)?.id
      ),
      ...node.equipmentSettings.map(
        (equipment) => getRequiredLinkTagSchema(equipment.equipmentVersion.dataType)?.id
      ),
    ];

    for (const linkedSchemaId of allLinkedSchemaIds) {
      if (linkedSchemaId != null && tagSchemas.find(({id}) => id === linkedSchemaId) == null) {
        return ProcedureAccessLevel.READ_ONLY_HIDE_DETAILS;
      }
    }

    for (const analysisConfig of node.analysisTemplateConfigurations) {
      if (isInaccessibleAnalysisTemplateConfiguration(analysisConfig)) {
        return ProcedureAccessLevel.READ_ONLY_HIDE_DETAILS;
      }
    }
    for (const testDefinition of node.testDefinitions) {
      if (isInaccessibleProcedureTestDefinition(testDefinition)) {
        return ProcedureAccessLevel.READ_ONLY_HIDE_DETAILS;
      }
    }
  }

  return ProcedureAccessLevel.FULL_ACCESS;
}
